import {AxiosError, AxiosResponse} from "axios";
import {TfaSetupWizard} from "components/src/auth/TfaSetupWizard";
import {DialogCloseCause} from "components/src/common/Enums";
import {Loading} from "components/src/common/Loading";
import {logger} from "components/src/utils/Logger";
import {Utils} from "components/src/utils/Utils";
import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";

/**
 * Whether 2FA is required for- and already configured by the current user.
 */
export interface TfaProperties {
    tfaRequired: boolean;
    tfaEnabled: boolean;
}

interface TfaSetupRequiredProps {
    children: React.ReactNode;
    /**
     * Fetches whether 2FA is required for- and already configured by the current user. This must
     * talk to the same back-end the app is authenticated against (e.g. portal vs company portal),
     * so the operations are injected by the owning app rather than hard-wired to a single client.
     */
    fetchTfaProperties: () => Promise<TfaProperties>;
    is2FAEnabledFunc: () => Promise<AxiosResponse<boolean>>;
    generateSecretFunc: () => Promise<AxiosResponse<string>>;
    createSecretQRCodeFunc: (secret: string) => Promise<AxiosResponse<string>>;
    set2FAEnabledFunc: (enabled: boolean) => Promise<AxiosResponse<void>>;
    validateOTPFunc: (otpCode: string, secret: string) => Promise<AxiosResponse<void>>;
    setSecretFunc: (secret: string) => Promise<AxiosResponse<void>>;
}

/**
 * Wraps authenticated app content. When 2FA is required and the current user has not yet
 * configured 2FA, the 2FA setup wizard is shown instead of the app. The wizard cannot be
 * dismissed until setup is complete.
 */
export const TfaSetupRequired = (
    {
        children,
        fetchTfaProperties,
        is2FAEnabledFunc,
        generateSecretFunc,
        createSecretQRCodeFunc,
        set2FAEnabledFunc,
        validateOTPFunc,
        setSecretFunc
    }: TfaSetupRequiredProps) =>
{
    const [loading, setLoading] = useState(true);
    const [tfaRequired, setTfaRequired] = useState(false);
    const [tfaEnabled, setTfaEnabled] = useState(false);

    const navigate = useNavigate();

    const refreshTfaProperties = () => {
        setLoading(true);
        fetchTfaProperties()
            .then(properties => {
                setTfaRequired(properties.tfaRequired);
                setTfaEnabled(properties.tfaEnabled);
            })
            .catch((err: Error | AxiosError) => {
                // Without this the failure is swallowed and the wizard silently never shows.
                logger.error("Error fetching 2FA properties", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        refreshTfaProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading) {
        return <Loading/>;
    }

    if (tfaRequired && !tfaEnabled) {
        return (
            <TfaSetupWizard
                is2FAEnabledFunc={is2FAEnabledFunc}
                generateSecretFunc={generateSecretFunc}
                createSecretQRCodeFunc={createSecretQRCodeFunc}
                set2FAEnabledFunc={set2FAEnabledFunc}
                validateOTPFunc={validateOTPFunc}
                setSecretFunc={setSecretFunc}
                tfaRequired={true}
                onClose={(closeCause: DialogCloseCause) => {
                    if (closeCause === DialogCloseCause.Succes) {
                        refreshTfaProperties();
                    }
                    // On Cancel: do not update state — wizard stays mounted because
                    // tfaRequired && !tfaEnabled is still true.
                }}
            />
        );
    }

    return <>{children}</>;
};
